import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, PhoneIncoming, PhoneOutgoing, BellOff, Bell } from "lucide-react";
import { Kpi } from "@/components/chef/pointage/shared";
import { Softphone } from "@/components/talk/Softphone";
import { CallsTable } from "@/components/talk/CallsTable";

// Phase 85 — PAG Talk côté salarié : « Mon poste » (appels entrants/sortants, disponibilité, mes appels).
export default function SalarieTalk() {
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [calls, setCalls] = useState(null);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/salarie/talk"); setData(d); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    try { const { data: c } = await api.get("/salarie/talk/calls", { params: { limit: 50 } }); setCalls(c); } catch { setCalls({ items: [] }); }
  }, []);
  const loadLive = useCallback(async () => { try { const { data: d } = await api.get("/talk/live"); setLive(d); } catch { /* pas de poste */ } }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!data?.enabled) return undefined; loadLive(); const i = setInterval(loadLive, 10000); return () => clearInterval(i); }, [data?.enabled, loadLive]);

  const toggleDnd = async () => {
    const next = data.agent.status === "dnd" ? "available" : "dnd";
    try { await api.put("/salarie/talk/status", { status: next }); toast.success(next === "dnd" ? "Mode « ne pas déranger » activé" : "Vous recevez à nouveau les appels"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-full" data-testid="salarie-talk-page">
      <PageHeader eyebrow="PAG Talk" title="Mon poste téléphonique" sub="Recevez et passez les appels de l'entreprise depuis votre navigateur. Gardez cette page ouverte pour être joignable ; activez « ne pas déranger » pendant vos pauses."
        right={data?.agent && <Button variant="outline" onClick={toggleDnd} className={`h-11 ${data.agent.status === "dnd" ? "border-[#E60000] text-[#E60000]" : ""}`} data-testid="talk-dnd-toggle">{data.agent.status === "dnd" ? <><BellOff className="h-4 w-4 mr-2" />Ne pas déranger</> : <><Bell className="h-4 w-4 mr-2" />Disponible</>}</Button>} />
      <div className="p-6 lg:p-10 space-y-6">
        {data && !data.enabled && (
          <div className="border-2 border-[#FFD700] bg-[#FFFBEB] p-5 flex items-center gap-4" data-testid="talk-blocked">
            <Lock className="h-6 w-6 text-[#B45309]" />
            <div className="text-sm">{!data.option?.subscribed ? "La téléphonie PAG Talk n'est pas activée pour votre entreprise." : !data.agent ? "Aucun poste téléphonique ne vous est attribué pour l'instant : demandez à votre gérant." : !data.number ? "Votre entreprise n'a pas encore choisi son numéro professionnel." : "Téléphonie en cours d'activation par PAG."}</div>
          </div>
        )}
        {data && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="talk-sal-kpis">
            <Kpi label="Numéro de l'entreprise" value={data.number?.display || "—"} dark testid="talk-sal-number" />
            <Kpi label="Mon poste" value={data.agent?.extension || "—"} sub={data.agent?.status === "dnd" ? "Ne pas déranger" : "Disponible"} testid="talk-sal-ext" />
            <Kpi label="Appels pris (mois)" value={data.stats.answered} icon={PhoneIncoming} testid="talk-sal-answered" />
            <Kpi label="Appels émis (mois)" value={data.stats.outbound} icon={PhoneOutgoing} testid="talk-sal-outbound" />
          </div>
        )}
        {data?.enabled && <Softphone enabled queue={live?.queue} agents={live?.agents || []} myIdentity={data.identity} onEvent={() => setTimeout(load, 1500)} />}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] mb-2">Mes appels</div>
          <CallsTable items={calls?.items} emptyLabel="Aucun appel traité pour le moment." />
        </div>
      </div>
    </div>
  );
}
